import type { ReactNode } from 'react'
import * as Sentry from '@sentry/react'

/**
 * Top-level error boundary. Wraps the app so any uncaught React render error
 * is reported to Sentry and the user sees a recoverable fallback instead of a
 * blank screen.
 */
export function AppErrorBoundary({ children }: { children: ReactNode }) {
  return (
    <Sentry.ErrorBoundary
      fallback={({ resetError }) => (
        <div className="flex min-h-screen items-center justify-center bg-background p-6">
          <div className="bg-card border-border max-w-md rounded-lg border p-8 text-center shadow-sm">
            <div className="mb-3 text-4xl">⚠️</div>
            <h2 className="text-foreground mb-2 text-lg font-semibold">
              Something went wrong
            </h2>
            <p className="text-muted-foreground mb-6 text-sm">
              The app hit an unexpected error. Reloading usually fixes it.
            </p>
            <button
              type="button"
              onClick={() => {
                resetError()
                window.location.reload()
              }}
              className="bg-primary text-primary-foreground rounded-md px-4 py-2 text-sm font-medium hover:opacity-90"
            >
              Reload app
            </button>
          </div>
        </div>
      )}
    >
      {children}
    </Sentry.ErrorBoundary>
  )
}
